import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { LogoutRoute } from '@/routes/RouteHelpers';
import { useLoginQuery } from '@/hooks/queries/useLoginQuery';
import { NoAccess } from '@/components/NoAccess';
import type { ModulePermission } from '@/data/teamData';
import DemosChecked from '@/components/DemosChecked';


interface PrivateRoutesProps {
  onLogout: () => void;
}

const Dashboard = lazy(() => import('@/components/Dashboard').then((module) => ({ default: module.Dashboard })));
const Merchants = lazy(() => import('@/components/Merchants').then((module) => ({ default: module.Merchants })));
const MerchantOnboarding = lazy(() =>
  import('@/components/MerchantOnboarding').then((module) => ({ default: module.MerchantOnboarding }))
);
const Locations = lazy(() => import('@/components/Locations').then((module) => ({ default: module.Locations })));
const FreeTrials = lazy(() => import('@/components/FreeTrials').then((module) => ({ default: module.FreeTrials })));
const Reviews = lazy(() => import('@/components/Reviews').then((module) => ({ default: module.Reviews })));
const SMSAnalytics = lazy(() => import('@/components/SMSAnalytics').then((module) => ({ default: module.SMSAnalytics })));
const Communications = lazy(() =>
  import('@/components/Communications').then((module) => ({ default: module.Communications })),
);
const PlatformActivity = lazy(() =>
  import('@/components/PlatformActivity').then((module) => ({ default: module.PlatformActivity }))
);
const Subscriptions = lazy(() =>
  import('@/components/Subscriptions').then((module) => ({ default: module.Subscriptions }))
);
const Revenue = lazy(() => import('@/components/Revenue').then((module) => ({ default: module.Revenue })));
const Transactions = lazy(() =>
  import('@/components/Transactions').then((module) => ({ default: module.Transactions }))
);
const Refunds = lazy(() => import('@/components/Refunds').then((module) => ({ default: module.Refunds })));
const Instagram = lazy(() => import('@/components/Instagram').then((module) => ({ default: module.Instagram })));
const Visitors = lazy(() => import('@/components/Visitors').then((module) => ({ default: module.Visitors })));
const ReviewPlatforms = lazy(() =>
  import('@/components/ReviewPlatforms').then((module) => ({ default: module.ReviewPlatforms }))
);
const Support = lazy(() => import('@/components/Support').then((module) => ({ default: module.Support })));
const Team = lazy(() => import('@/components/Team').then((module) => ({ default: module.Team })));
const SMSTemplates = lazy(() =>
  import('@/components/SMSTemplates').then((module) => ({ default: module.SMSTemplates }))
);
const InAppMessages = lazy(() =>
  import('@/components/InAppMessages').then((module) => ({ default: module.InAppMessages }))
);
const ServicesHub = lazy(() => import('@/components/ServicesHub').then((module) => ({ default: module.ServicesHub })));
const Testimonials = lazy(() =>
  import('@/components/Testimonials').then((module) => ({ default: module.Testimonials }))
);
const SMSSchedules = lazy(() =>
  import('@/components/SMSSchedules').then((module) => ({ default: module.SMSSchedules }))
);
const StaffAccess = lazy(() => import('@/components/StaffAccess').then((module) => ({ default: module.StaffAccess })));
const Funnels = lazy(() => import('@/components/Funnels').then((module) => ({ default: module.Funnels })));
const TargetLeads = lazy(() => import('@/components/TargetLeads').then((module) => ({ default: module.TargetLeads })));
const TrialFunnel = lazy(() => import('@/components/TrialFunnel').then((module) => ({ default: module.TrialFunnel })));
const Enquiries = lazy(() =>
  import('@/components/Enquiries').then((module) => ({ default: module.Enquiries }))
);
const Referrals = lazy(() => import('@/components/Referrals').then((module) => ({ default: module.Referrals })));
const RequestReferrals = lazy(() =>
  import('@/components/RequestReferrals').then((module) => ({ default: module.RequestReferrals }))
);
const ReferralTimeline = lazy(() =>
  import('@/components/ReferralTimeline').then((module) => ({ default: module.ReferralTimeline }))
);
const Settings = lazy(() => import('@/components/Settings').then((module) => ({ default: module.Settings })));
const Testing = lazy(() => import('@/components/Testing').then((module) => ({ default: module.Testing })));
const FraudMonitoring = lazy(() => import('@/components/FraudMonitoring').then((module) => ({ default: module.FraudMonitoring })));
export function PrivateRoutes({ onLogout }: PrivateRoutesProps) {
  const { data: loginUser } = useLoginQuery();
  const userPermissions: string[] = Array.isArray((loginUser as any)?.permissions)
    ? (loginUser as any).permissions
    : [];

  const canAccess = (required?: ModulePermission[]) => {
    if (!required || required.length === 0) return true;
    return required.some((p) => userPermissions.includes(p));
  };

  const protect = (element: React.ReactElement, required?: ModulePermission[]) =>
    canAccess(required) ? element : <NoAccess backTo="/merchants" />;

  return (
    <DashboardLayout>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Merchants />} />
          {/* <Route path="/dashboard" element={<Dashboard />} /> */}
          <Route path="/merchants" element={protect(<Merchants />, ['merchants'])} />
          <Route path="/merchant-onboarding" element={protect(<MerchantOnboarding />, ['merchant_onboarding'])} />
          <Route path="/locations" element={protect(<Locations />, ['locations'])} />
          <Route path="/free-trials" element={protect(<FreeTrials />, ['free_trials'])} />

          <Route path="/reviews" element={protect(<Reviews />, ['reviews'])} />
          <Route path="/sms-analytics" element={protect(<SMSAnalytics />, ['sms_analytics'])} />
          {/*
            Deliberately not permission-gated. These module permissions are a UI
            filter only — the admin API enforces nothing — so gating this bought
            no security while making the page invisible to every admin whose
            session predates the permission being added. Any signed-in admin can
            see the push rules and switch one off.
          */}
          <Route path="/communications" element={<Communications />} />
          {/* The page grew a customer-channel section and was renamed; old links keep working. */}
          <Route path="/push-notifications" element={<Navigate to="/communications" replace />} />
          <Route path="/sms-schedules" element={protect(<SMSSchedules />, ['sms_schedules'])} />
          <Route path="/sms-templates" element={protect(<SMSTemplates />, ['sms_templates'])} />
          <Route path="/testing" element={protect(<Testing />, ['testing'])} />
          <Route path="/platform-activity" element={<PlatformActivity />} />
          <Route path="/subscriptions" element={protect(<Subscriptions />, ['subscriptions'])} />
          <Route path="/revenue" element={protect(<Revenue />, ['revenue'])} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/instagram" element={protect(<Instagram />, ['instagram'])} />
          <Route path="/visitors" element={protect(<Visitors />, ['visitors'])} />
          <Route path="/review-platforms" element={protect(<ReviewPlatforms />, ['review_platforms'])} />
          <Route path="/support" element={protect(<Support />, ['support'])} />
          <Route path="/enquiries" element={<Enquiries />} />
          <Route path="/team" element={protect(<Team />, ['team'])} />
          <Route path="/services" element={<ServicesHub />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/in-app-messages" element={<InAppMessages />} />
          <Route path="/funnels" element={<Funnels />} />
          {/* Kept so bookmarks and older links still land somewhere. */}
          <Route path="/leads" element={<Funnels />} />
          <Route path="/target-leads" element={<TargetLeads />} />
          <Route path="/demos-checked" element={<DemosChecked />} />
          <Route path="/leads/funnel" element={<TrialFunnel />} />
          <Route path="/staff-access" element={<StaffAccess />} />
          <Route path="/settings" element={protect(<Settings />, ['settings'])} />
          <Route path="/fraud-monitoring" element={protect(<FraudMonitoring />, ['fraud_monitoring'])} />
          <Route path="/referrals" element={<Referrals />} />
          <Route path="/request-referrals" element={<RequestReferrals />} />
          <Route path="/referral-timeline/:id" element={<ReferralTimeline />} />
          <Route path="/logout" element={<LogoutRoute onLogout={onLogout} />} />
          <Route path="*" element={<Navigate to="/merchants" replace />} />
        </Routes>
      </Suspense>
    </DashboardLayout>
  );
}
